import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle,
  Fingerprint,
  KeyRound,
  Loader2,
  Mail,
  Send,
  XCircle,
} from "lucide-react";

import { getUserProfile } from "@/app/auth/authService";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { runMailTest, type MailTestMode, type MailTestResult } from "@/services/mailDiagnosticApi";

import {
  FINGERPRINT_COMPARISON_LABEL,
  STAGE_LABELS,
  STAGE_STATUS_BADGE,
  TONE_CLASS,
  compareFingerprints,
  formatDuration,
  outcomeHint,
  outcomeLabel,
  sendgridKeySummary,
  stageStatus,
  transportLabel,
} from "./mailDiagnostic";

/**
 * Diagnostic e-mail — la clé SendGrid est-elle encore acceptée, les mails partent-ils ?
 *
 * Tout passe par backoffice -> back/ (`/api/internal/mail/test`) : la page ne voit
 * jamais une clé, seulement des empreintes (8 premiers caractères du SHA-256).
 */
export default function MailDiagnosticPage() {
  const navigate = useNavigate();
  const [recipient, setRecipient] = useState("");
  const [running, setRunning] = useState<MailTestMode | null>(null);
  const [result, setResult] = useState<MailTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Prefill with the logged-in admin: a test goes to oneself by default.
    getUserProfile()
      .then((profile) => {
        const email = typeof profile?.email === "string" ? profile.email : "";
        if (email) setRecipient((current) => current || email);
      })
      .catch(() => undefined);
  }, []);

  const run = async (mode: MailTestMode) => {
    setRunning(mode);
    setError(null);
    setResult(null);
    try {
      const r =
        mode === "send"
          ? await runMailTest(navigate, "send", recipient.trim() || undefined)
          : await runMailTest(navigate, "auth");
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(null);
    }
  };

  const hint = result ? outcomeHint(result.outcome) : null;
  const sendgrid = result ? sendgridKeySummary(result.sendgrid_api_key) : null;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Diagnostic e-mail</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-4xl">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <KeyRound className="h-4 w-4" /> Jeton du relais SMTP (SendGrid)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                Les e-mails transactionnels (invitations, relances, RGPD, rendez-vous) partent par
                le relais SMTP de SendGrid. Le jeton est la clé API SendGrid stockée dans{" "}
                <code className="text-xs bg-gray-100 px-1 rounded">SMTP_PASSWORD</code>{" "}
                (utilisateur <code className="text-xs bg-gray-100 px-1 rounded">apikey</code>). La
                vérification se connecte au relais et s'authentifie, sans envoyer d'e-mail.
              </p>
              <Button onClick={() => run("auth")} disabled={running !== null}>
                {running === "auth" ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-1" />
                )}
                Vérifier le jeton
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" /> Envoyer un e-mail de test
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="space-y-1.5 max-w-md">
                <Label htmlFor="mail-test-recipient">Destinataire</Label>
                <Input
                  id="mail-test-recipient"
                  type="email"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="prenom.nom@recrutauto.fr"
                />
                <p className="text-xs text-muted-foreground">
                  Seuls les domaines internes autorisés (
                  <code className="bg-gray-100 px-1 rounded">MAIL_TEST_ALLOWED_DOMAINS</code> côté
                  back) peuvent recevoir un e-mail de test. 5 tests par minute au plus.
                </p>
              </div>
              <Button variant="outline" onClick={() => run("send")} disabled={running !== null}>
                {running === "send" ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-1" />
                )}
                Envoyer un e-mail de test
              </Button>
            </CardContent>
          </Card>

          {error && (
            <div className="px-3 py-2 rounded text-sm bg-red-50 text-red-800 border border-red-200">
              {error}
            </div>
          )}

          {result && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Résultat — {result.mode === "auth" ? "vérification du jeton" : "envoi de test"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 text-sm">
                <div
                  className={`flex items-start gap-2 px-3 py-2 rounded border ${
                    result.ok
                      ? "bg-green-50 text-green-800 border-green-200"
                      : "bg-red-50 text-red-800 border-red-200"
                  }`}
                >
                  {result.ok ? (
                    <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  ) : (
                    <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  )}
                  <div className="space-y-1">
                    <div className="font-medium">{outcomeLabel(result.outcome)}</div>
                    {hint && <div className="text-xs">{hint}</div>}
                    {result.mode === "send" && result.ok && (
                      <div className="text-xs">
                        Accepté par le relais pour {result.recipient} — ce n'est pas encore la
                        preuve de la réception : vérifier la boîte (et les indésirables).
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    Étapes de la session SMTP
                  </div>
                  <table className="w-full text-sm border rounded">
                    <thead>
                      <tr className="border-b bg-gray-50 text-left text-xs text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Étape</th>
                        <th className="px-3 py-2 font-medium">Statut</th>
                        <th className="px-3 py-2 font-medium">Code SMTP</th>
                        <th className="px-3 py-2 font-medium">Durée</th>
                        <th className="px-3 py-2 font-medium">Détail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.stages.map((s) => {
                        const badge = STAGE_STATUS_BADGE[stageStatus(s)];
                        return (
                          <tr key={s.stage} className="border-b last:border-0 align-top">
                            <td className="px-3 py-2 whitespace-nowrap">{STAGE_LABELS[s.stage]}</td>
                            <td className="px-3 py-2">
                              <span
                                className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${badge.className}`}
                              >
                                {badge.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{s.smtp_code ?? "—"}</td>
                            <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                              {formatDuration(s.duration_ms)}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs break-all">
                              {s.detail ?? "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {result.message_id && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Message-ID : <span className="font-mono">{result.message_id}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-muted-foreground">Relais</div>
                    <div className="font-mono text-xs break-all">
                      {result.smtp.host ?? "—"}:{result.smtp.port ?? "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Transport</div>
                    <div className="text-xs">{transportLabel(result.smtp)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Utilisateur</div>
                    <div className="font-mono text-xs">{result.smtp.user ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Expéditeur (SMTP_FROM)</div>
                    <div className="font-mono text-xs break-all">{result.smtp.sender ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">SMTP_PASSWORD</div>
                    <div className="text-xs">{result.smtp.password_set ? "Posé" : "Absent"}</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    Clé Web API SendGrid (SENDGRID_API_KEY, DKIM)
                  </div>
                  {sendgrid && (
                    <div className={`flex items-center gap-1.5 text-sm ${TONE_CLASS[sendgrid.tone]}`}>
                      {sendgrid.tone === "ok" ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : sendgrid.tone === "error" ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <AlertTriangle className="h-4 w-4" />
                      )}
                      {sendgrid.label}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
                    <Fingerprint className="h-3.5 w-3.5" /> Empreintes des clés (SHA-256, 8
                    premiers caractères)
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-muted-foreground">SMTP_PASSWORD</div>
                      <div className="font-mono text-xs">
                        {result.fingerprints.smtp_password ?? "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">SENDGRID_API_KEY</div>
                      <div className="font-mono text-xs">
                        {result.fingerprints.sendgrid_api_key ?? "—"}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 text-sm">
                    {FINGERPRINT_COMPARISON_LABEL[compareFingerprints(result.fingerprints)]}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Pour comparer avec les copies Keycloak et n8n : empreinte locale ={" "}
                    <code className="bg-gray-100 px-1 rounded">
                      printf '%s' "$CLE" | sha256sum | cut -c1-8
                    </code>{" "}
                    (runbook internal-docs/runbooks/sendgrid-key-rotation.md).
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
